import { ChangeEventHandler, FormEventHandler, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import useProfile from "../../hooks/useProfile";
import Alert from "../../components/alert/Alert";
import ProfileIcon from "../../components/icons/Profile";
import Button from "../../components/ui/button";
import { isEmail, isEmpty, isStrongPassword } from "validator";
import { PASS_VALIDATORS } from "../../validators/validators";
import { UserRole } from "../../types/user";
import { Helmet } from "react-helmet-async";
import PageSectionHeader from "../../components/PageSectionHeader";

type ProfileForm = {
  firstName: string;
  lastName: string;
  email: string;
  password?: {
    new: string;
    confirm: string;
  };
}

const Profile = () => {
  const intl = useIntl();
  const {profile, getProfile, updateSuccess, updateError, updateProfile} = useProfile();
  const [profileForm, setProfileForm] = useState<ProfileForm|null>(null);

  const [wasChanged, setWasChanged] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const isValidForm = useMemo(() => {
    if (!profileForm) return false;

    const {email, firstName, lastName, password} = profileForm;
    const isValidInfo = isEmail(email) && !isEmpty(firstName) && !isEmpty(lastName);
    const isNoPassword = !password || ( isEmpty(password.new) && isEmpty(password.confirm) );
    const isValidPassword = password && isStrongPassword(password.new, PASS_VALIDATORS) && password.new === password.confirm;

    return isValidInfo && (isNoPassword || isValidPassword)
  }, [profileForm])

  useEffect(() => {
    getProfile();
  }, [getProfile])

  // Reset form state after fetching profile
  useEffect(() => {
    if (profile) {
      const {firstName, lastName, email} = profile;
      setProfileForm({firstName, lastName, email, password: undefined});
      setWasChanged(false);
      setWasSubmitted(false);
    }
  }, [profile])

  // Reset form state after update
  useEffect(() => {
    if (updateSuccess) {
      setWasChanged(false);
      setWasSubmitted(false);
      setProfileForm(prev => prev ? {...prev, password: undefined} : null)
    }
  }, [updateSuccess])

  // Normal Input change
  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setProfileForm(form => {
      const updateForm: ProfileForm = form ? form : { email: '', firstName: '', lastName: ''};
      return {...updateForm, [e.target.name]: e.target.value}
    });
    setWasChanged(true);
  }

  const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setProfileForm(form => {
      const updateForm: ProfileForm = form ?
        ( form.password ? form : {...form, password: {new: '', confirm: ''}} ) :
        { email: '', firstName: '', lastName: '', password: {new: '', confirm: ''} };

      return {
        ...updateForm,
        password: {
          ...updateForm.password!,
          [e.target.name]: e.target.value
        }
      }
    });
    setWasChanged(true);
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (profileForm && isValidForm) {
      const {password, ...profileInfo} = profileForm;
      if (password && password.new.length > PASS_VALIDATORS.minLength!) {
        updateProfile({...profileInfo, password: password.new});
      } else {
        updateProfile(profileInfo);
      }
    }
    setWasSubmitted(true);
  }

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: 'profile.documentTitle' },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>        
      </Helmet>
      <PageSectionHeader className="mb-[60px]">
        <div className="flex gap-2 items-center">
          { profile?.role === UserRole.operator && <ProfileIcon className="size-[18px]" /> }
          <h2 className="text-xl font-normal text-shadow">
            {intl.formatMessage({ id: 'profile.heading' })}
          </h2>
        </div>
      </PageSectionHeader>
      
      <form className="flex flex-col gap-[50px]" onSubmit={handleSubmit} noValidate>
        <section>
          <h3 className="mb-5 text-sm text-shadow font-bold">
            {intl.formatMessage({ id: 'profile.personalInfo' })}
          </h3>
          <div className="grid md:grid-cols-2 gap-x-5 gap-y-3.5">
            <div>
              <label className="mb-1.5 text-sm" htmlFor="lastName">
                {intl.formatMessage({ id: 'profile.lastName' })}
              </label>
              <input id="lastName" type="text" name="lastName"
                className="input mb-0"
                placeholder={intl.formatMessage({ id: 'profile.lastNamePlaceholder' })}
                value={profileForm?.lastName || ''} onChange={handleChange} />
                {
                  wasSubmitted && profileForm && isEmpty(profileForm.lastName) &&
                  <span className="text-red-500 text-xs">
                    {intl.formatMessage(
                      { id: 'validators.required' },
                      { name: intl.formatMessage({ id: 'profile.lastName' }) }
                    )}
                  </span>
                }
            </div>
            <div>
              <label className="mb-1.5 text-sm" htmlFor="firstName">
                {intl.formatMessage({ id: 'profile.firstName' })}
              </label>
              <input id="firstName" type="text" name="firstName"
                className="input mb-0"
                placeholder={intl.formatMessage({ id: 'profile.firstNamePlaceholder' })}
                value={profileForm?.firstName || ''} onChange={handleChange} />
                {
                  wasSubmitted && profileForm && isEmpty(profileForm.firstName) &&
                  <span className="text-red-500 text-xs">
                    {intl.formatMessage(
                      { id: 'validators.required' },
                      { name: intl.formatMessage({ id: 'profile.firstName' }) }
                    )}
                  </span>
                }
            </div>
            <div>
              <label className="mb-1.5 text-sm" htmlFor="email">
                {intl.formatMessage({ id: 'profile.email' })}
              </label>
              <input id="email" autoComplete="username" type="email" name="email"
                className="input mb-0"
                placeholder={intl.formatMessage({ id: 'profile.emailPlaceholder' })}
                value={profileForm?.email || ''} onChange={handleChange} />
                {
                  wasSubmitted && profileForm && (
                    <>
                    {
                      isEmpty(profileForm.email) ?
                      <span className="text-red-500 text-xs inline-block">
                        {intl.formatMessage(
                          { id: 'validators.required' },
                          { name: intl.formatMessage({ id: 'profile.email' }) }
                        )}
                      </span> :
                      !isEmail(profileForm.email) &&
                      <span className="text-red-500 text-xs inline-block">
                        {intl.formatMessage({ id: 'profile.emailInvalid' })}
                      </span>
                    }
                    </>
                  )
                }
            </div>
          </div>
        </section>

        <section>
          <h3 className="mb-5 text-sm text-shadow font-bold">
            {intl.formatMessage({ id: 'profile.security' })}
          </h3>
          <div className="flex flex-col md:flex-row gap-5">
            <div className="w-full">
              <label className="mb-1.5 text-sm" htmlFor="new-password">
                {intl.formatMessage({ id: 'profile.newPassword' })}
              </label>
              <input id="new-password" type="password" name="new"
                className="input mb-0" autoComplete="new-password"
                placeholder="******************"
                value={profileForm?.password?.new || ''}
                onChange={handlePasswordChange}/>
                {
                  wasSubmitted && profileForm?.password && (
                    <>
                    {
                      !isEmpty(profileForm.password.confirm) && isEmpty(profileForm.password.new) ?
                      <span className="text-red-500 text-xs">
                        {intl.formatMessage(
                          { id: 'validators.required' },
                          { name: intl.formatMessage({ id: 'profile.newPassword' }) }
                        )}
                      </span> :
                      !isStrongPassword(profileForm.password.new, PASS_VALIDATORS) &&
                      <span className="text-red-500 text-xs">
                        {intl.formatMessage(
                          { id: 'validators.passwordRequirements' },
                          { minLength: PASS_VALIDATORS.minLength }
                        )}
                      </span>
                    }
                    </>
                  )
                }
            </div>
            <div className="w-full">
              <label className="mb-1.5 text-sm" htmlFor="confirm-password">
                {intl.formatMessage({ id: 'profile.confirmNewPassword' })}
              </label>
              <input id="confirm-password" type="password" name="confirm"
                className="input mb-0" autoComplete="new-password"
                placeholder="******************"
                value={profileForm?.password?.confirm || ''}
                onChange={handlePasswordChange}/>
                {
                  wasSubmitted && profileForm?.password && (
                    <>
                    {
                      !isEmpty(profileForm.password.new) && isEmpty(profileForm.password.confirm) ?
                      <span className="text-red-500 text-xs inline-block">
                        {intl.formatMessage(
                          { id: 'validators.requiredFeminine' },
                          { name: intl.formatMessage({ id: 'profile.confirmNewPassword' }) }
                        )}
                      </span> :
                      !isEmpty(profileForm.password.confirm) && profileForm.password.new !== profileForm.password.confirm &&
                      <span className="text-red-500 text-xs inline-block">
                        {intl.formatMessage({ id: 'validators.passwordMismatch' })}
                      </span>
                    }
                    </>
                  )
                }
            </div>
          </div>
        </section>

        <Button type="submit" width="fit" theme="primary" className="px-4 md:px-7 !bg-[#E94E1B] hover:!bg-[#d34515] focus-visible:!ring-[#E94E1B]"
          disabled={!wasChanged || (wasSubmitted && !isValidForm)}>
          {intl.formatMessage({ id: 'profile.submit' })}
        </Button>
      </form>

      {
        updateSuccess &&
        <Alert
          type="success"
          message={intl.formatMessage({ id: 'profile.saveSuccess' })}
          className="my-5 w-fit"
        />
      }

      {
        updateError &&
        <Alert type="error" message={updateError.message} className="my-5 w-fit"/>
      }

    </>
  )
}

export default Profile;
